import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import AppNav from '../../components/app/AppNav';
import { Service } from '../../types/services';
import { SERVICE_EMOJI, SERVICE_GRADIENT } from '../../constants/services';
import { apiGet } from '../../api/api';
import { useAuth } from '../../context/AuthContext';
import { useUserLocation, calcDistance, formatDistance } from '../../hooks/useUserLocation';
import { slugify, extractId } from '../../utils/slug';

export default function AppServiceDetail() {
  const { id: slugParam } = useParams<{ id: string }>();
  const id = slugParam ? extractId(slugParam) : undefined;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const community = (location.state as { community?: string } | null)?.community || searchParams.get('community') || undefined;
  const { authUser, loading: authLoading } = useAuth();
  const userCoords = useUserLocation();

  const [service, setService] = useState<Service | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imgIndex, setImgIndex] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!id || authLoading) return;
    const params: Record<string, string> = { id };
    if (!authUser && community) params.community = community;
    const endpoint = authUser ? '/api_service_details' : '/api_service_details-public';
    apiGet<{ data: Service }>(endpoint, params, !!authUser)
      .then((res) => setService(res.data))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load service'))
      .finally(() => setLoading(false));
  }, [id, community, authUser, authLoading]);

  const handleShare = () => {
    const link = service
      ? `https://sahayy.com/services/${slugify(service.title)}-${service.id}?community=${encodeURIComponent(service.community || '')}`
      : window.location.href;
    const emoji = service ? (SERVICE_EMOJI[service.category] || '🛠️') : '';
    const message = service
      ? `🛠️ ${service.title}\n${emoji} ${service.category}${service.location ? `\n📍 ${service.location}` : ''}${service.community ? `\n🏘️ ${service.community}` : ''}\n\nFind on Sahayy 👇\n${link}`
      : link;
    navigator.clipboard.writeText(message).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
      <AppNav />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 64px)', flexDirection: 'column', gap: '0.75rem' }}>
        <div style={{ width: '36px', height: '36px', border: '3px solid #f0f0f0', borderTop: '3px solid #FF385C', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );

  if (error || !service) return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
      <AppNav />
      <div style={{ maxWidth: '480px', margin: '6rem auto', padding: '0 1.5rem', textAlign: 'center' }}>
        <p style={{ fontSize: '2.5rem', margin: '0 0 1rem' }}>🛠️</p>
        <h2 style={{ color: '#111', fontWeight: 700, margin: '0 0 0.5rem' }}>Service not found</h2>
        <p style={{ color: '#6b7280', margin: '0 0 2rem' }}>{error || 'This service does not exist.'}</p>
        <button onClick={() => navigate('/services')} style={{ padding: '0.75rem 2rem', backgroundColor: '#111', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>← Back to Services</button>
      </div>
    </div>
  );

  const isCreator = authUser?.userId === service.user_id;
  const emoji = SERVICE_EMOJI[service.category] || '🛠️';
  const gradient = SERVICE_GRADIENT[service.category] || SERVICE_GRADIENT['Other'];
  const images = service.images?.filter(Boolean) || [];
  const hasImage = images.length > 0;
  const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const deepLink = `sahayy://services/${service.id}?community=${encodeURIComponent(service.community || '')}`;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
      <AppNav />

      <div style={{ maxWidth: '1080px', margin: '0 auto', padding: '1.25rem 1.5rem 8rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.875rem' }}>
          <button onClick={() => navigate('/services')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.375rem', color: '#111', fontWeight: 600, fontSize: '0.9rem', padding: 0 }}>← All Services</button>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {isCreator && (
              <button onClick={() => navigate(`/services/edit?id=${service.id}&community=${encodeURIComponent(service.community || '')}`)}
                style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '20px', padding: '0.35rem 1rem', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: '#111' }}>✏️ Edit</button>
            )}
            <button onClick={handleShare} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '20px', padding: '0.35rem 1rem', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: copied ? '#16a34a' : '#111' }}>{copied ? '✓ Copied' : '↑ Share'}</button>
          </div>
        </div>

        {/* Photo grid */}
        <div className="hero-grid" style={{ display: 'grid', gap: '6px', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '420px', borderRadius: '16px', overflow: 'hidden' }}>
          <div style={{ position: 'relative', overflow: 'hidden' }}>
            {images[0] ? <img src={images[0]} alt={service.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              : <div style={{ width: '100%', height: '100%', background: gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontSize: '6rem' }}>{emoji}</span></div>}
          </div>
          <div style={{ display: 'grid', gap: '6px', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr' }}>
            {[1, 2, 3, 4].map((idx) => (
              <div key={idx} style={{ position: 'relative', overflow: 'hidden' }}>
                {images[idx] ? <img src={images[idx]} alt={`${service.title} ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  : <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #f0f0ee 0%, #e8e6e3 100%)' }} />}
              </div>
            ))}
          </div>
        </div>

        {/* Mobile carousel */}
        <div className="hero-carousel" style={{ position: 'relative', width: '100%', aspectRatio: '16/9', borderRadius: '16px', overflow: 'hidden', backgroundColor: '#f3f4f6' }}>
          {hasImage ? <img key={imgIndex} src={images[imgIndex]} alt={service.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            : <div style={{ width: '100%', height: '100%', background: gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontSize: '5rem' }}>{emoji}</span></div>}
          {images.length > 1 && (
            <>
              <button onClick={() => setImgIndex(i => (i - 1 + images.length) % images.length)} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.88)', border: 'none', borderRadius: '50%', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1rem' }}>‹</button>
              <button onClick={() => setImgIndex(i => (i + 1) % images.length)} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.88)', border: 'none', borderRadius: '50%', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1rem' }}>›</button>
              <div style={{ position: 'absolute', bottom: '10px', left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: '5px' }}>
                {images.map((_, i) => <div key={i} onClick={() => setImgIndex(i)} style={{ width: i === imgIndex ? '16px' : '6px', height: '6px', borderRadius: '3px', backgroundColor: i === imgIndex ? '#fff' : 'rgba(255,255,255,0.5)', cursor: 'pointer', transition: 'all 0.2s' }} />)}
              </div>
            </>
          )}
        </div>

        {/* Content */}
        <div className="detail-columns" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 340px', gap: '4rem', alignItems: 'start' }}>
          <div>
            <div style={{ padding: '2rem 0 1.5rem', borderBottom: '1px solid #f0f0f0' }}>
              <h1 style={{ color: '#111', fontWeight: 800, fontSize: 'clamp(1.5rem,3vw,2rem)', margin: '0 0 0.75rem', letterSpacing: '-0.03em', lineHeight: 1.2 }}>{service.title}</h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap', color: '#6b7280', fontSize: '0.9rem' }}>
                <span style={{ fontWeight: 600, color: '#111' }}>{emoji} {service.category}</span>
                {service.location && (
                  <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(service.location)}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: 'inherit', textDecoration: 'none' }}>
                    📍 {service.location}
                    {userCoords && service.latitude && service.longitude && (
                      <span style={{ color: '#10b981', marginLeft: '6px', fontSize: '0.82rem', fontWeight: 600 }}>{formatDistance(calcDistance(userCoords.lat, userCoords.lng, service.latitude, service.longitude))}</span>
                    )}
                    {' '}<span style={{ color: '#FF385C', fontSize: '0.75rem', fontWeight: 600 }}>↗</span>
                  </a>
                )}
                {service.community && <span style={{ color: '#9ca3af' }}>{service.community}</span>}
              </div>
            </div>

            <div style={{ padding: '1.5rem 0', borderBottom: '1px solid #f0f0f0' }}>
              <h2 style={{ margin: '0 0 0.875rem', fontWeight: 700, fontSize: '1rem', color: '#111' }}>About this service</h2>
              <p style={{ color: '#374151', fontSize: '0.975rem', lineHeight: 1.8, margin: 0, whiteSpace: 'pre-line' }}>{service.description}</p>
              {service.tags && service.tags.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '1.25rem' }}>
                  {service.tags.map(tag => <span key={tag} style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', color: '#374151', fontSize: '0.8rem', padding: '4px 12px', borderRadius: '20px', fontWeight: 500 }}>#{tag}</span>)}
                </div>
              )}
            </div>

            {(service.contact_info || service.website) && (
              <div style={{ padding: '1.5rem 0' }}>
                <h2 style={{ margin: '0 0 1rem', fontWeight: 700, fontSize: '1rem', color: '#111' }}>Contact</h2>
                {service.contact_info && <p style={{ margin: '0 0 0.5rem', color: '#374151', fontSize: '0.95rem' }}>📞 {service.contact_info}</p>}
                {service.website && <a href={service.website} target="_blank" rel="noopener noreferrer" style={{ color: '#FF385C', fontSize: '0.95rem', textDecoration: 'none', fontWeight: 500 }}>🔗 {service.website}</a>}
              </div>
            )}
          </div>

          <div className="detail-sidebar" style={{ position: 'sticky', top: '80px', paddingTop: '2rem' }}>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '16px', padding: '1.5rem', boxShadow: '0 6px 30px rgba(0,0,0,0.08)' }}>
              <a href={isMobileDevice ? deepLink : 'https://sahayy.com/download'} target={isMobileDevice ? undefined : '_blank'} rel="noopener noreferrer" style={{ display: 'block', textAlign: 'center', padding: '0.875rem', background: 'linear-gradient(135deg,#FF385C,#e11d48)', color: '#fff', borderRadius: '10px', fontWeight: 600, fontSize: '0.95rem', textDecoration: 'none', marginBottom: '0.75rem', boxShadow: '0 4px 14px rgba(255,56,92,0.35)' }}>Contact in App →</a>

              <button onClick={handleShare} style={{ width: '100%', padding: '0.75rem', backgroundColor: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '10px', color: '#111', fontSize: '0.9rem', fontWeight: 500, cursor: 'pointer' }}>{copied ? '✓ Message copied!' : '↑ Share service'}</button>

              <div style={{ marginTop: '1.25rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '10px', textAlign: 'center' }}>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.82rem', color: '#374151', fontWeight: 600 }}>Get the full experience</p>
                <a href={isMobileDevice ? deepLink : 'https://sahayy.com/download'} target={isMobileDevice ? undefined : '_blank'} rel="noopener noreferrer" style={{ display: 'inline-block', backgroundColor: '#111', color: '#fff', padding: '0.5rem 1.25rem', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 600, textDecoration: 'none' }}>Open in App →</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mobile-bottom-bar" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, backgroundColor: '#fff', borderTop: '1px solid #f0f0f0', padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', zIndex: 50 }}>
        <a href={isMobileDevice ? deepLink : 'https://sahayy.com/download'} target={isMobileDevice ? undefined : '_blank'} rel="noopener noreferrer" style={{ flex: 1, padding: '0.875rem 1.75rem', background: 'linear-gradient(135deg,#FF385C,#e11d48)', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer', textDecoration: 'none', boxShadow: '0 4px 14px rgba(255,56,92,0.35)', textAlign: 'center' }}>Contact in App</a>
      </div>

      <style>{`
        @media (min-width: 768px) { .mobile-bottom-bar { display: none !important; } .hero-carousel { display: none !important; } }
        @media (max-width: 767px) { .hero-grid { display: none !important; } .detail-columns { display: block !important; } .detail-sidebar { display: none !important; } }
      `}</style>
    </div>
  );
}
